<template>
    <slot name="trigger">
        <button
            ref="triggerEl"
            class="tooltip-trigger"
            :class="classTooltipTrigger"
            :title
            @click="open"
        >
            <slot name="label"></slot>
        </button>
    </slot>

    <teleport to="#teleport-tooltip">
        <TransitionModal>
            <TooltipLayout
                v-if="isOpen"
                ref="tooltipEl"
                @close="close"
            >
                <template #title>
                    <slot name="title"></slot>
                </template>

                <template #content>
                    <slot name="content"></slot>
                </template>
            </TooltipLayout>
        </TransitionModal>
    </teleport>
</template>

<script setup lang="ts">
import { computed, useTemplateRef, watch } from 'vue'

import { useTooltip } from './useTooltip'
import { isCustomTooltip } from '@/directives/tooltip/tooltip'

import TransitionModal from '@/components/layout/modal/TransitionModal.vue'
import TooltipLayout from './TooltipLayout.vue'
import type { PageType } from '@/components/cms/component/content/ContentButton.vue'

const {
    small,
    inline,
    triggerTitle = undefined,
    pageType,
} = defineProps<{
    small?: boolean
    inline?: boolean
    triggerTitle?: string
    pageType: PageType
}>()

const emit = defineEmits<{
    toggle: [v: boolean]
}>()

const title = computed(() => triggerTitle && `CMS Path: ${triggerTitle}`)

const triggerEl = useTemplateRef('triggerEl')
const id = Symbol()
const { open: openTooltip, close, tooltip, tooltipEl } = useTooltip()

const open = () => {
    if (!triggerEl.value) {
        return
    }

    openTooltip({ type: 'tooltip-custom', id }, triggerEl.value, pageType)
}

const isOpen = computed(
    () => isCustomTooltip(tooltip.value) && tooltip.value.id === id
)

watch(isOpen, (open) => {
    emit('toggle', open)
})

const classTooltipTrigger = computed(() => ({
    inline,
    small,
}))
</script>

<style lang="scss" scoped>
.tooltip-trigger {
    @include tooltip-trigger;

    &.small {
        &::after {
            font-size: 13px;
            height: 16px;
            width: 16px;
            margin: 0 0 0 5px;
        }
    }

    &.inline {
        display: inline;
    }
}
</style>
